import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { AgencyClientsCapWidget } from './AgencyClientsCapWidget';
import { AgencyJobsCapWidget } from './AgencyJobsCapWidget';
import { AgencyTranscriptsWidget } from './AgencyTranscriptsWidget';
import { useAgencyAggregatedData } from '@/hooks/useAgencyAggregatedData';
import { useDashboardMetrics, type Period } from '@/hooks/useDashboardMetrics';
import { HeroMetricCard } from './HeroMetricCard';
import { FunnelChart } from './FunnelChart';
import { ChannelChart } from './ChannelChart';
import { TrendChart } from './TrendChart';
import { TimeInStateChart } from './TimeInStateChart';
import { TopPositionsTable, type TopPositionsRow } from './TopPositionsTable';
import { PortfolioAiCreditsWidget } from './PortfolioAiCreditsWidget';
import { PipelineStatusGrid } from './PipelineStatusGrid';
import { ResponsiveTable, TABLE_COL, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { ScoreBadge, StatusBadge, ChannelBadge } from '@/components/shared/Badges';
import { formatRelative } from '@/lib/dateUtils';
import {
  Briefcase, Megaphone, Users, Percent, Hourglass, Target, Loader2,
} from 'lucide-react';
import type { Application } from '@/types';
import { isApplicationArchived } from '@/lib/applicationArchive';

/**
 * Agency Plan Dashboard — aggregated metrics across all client companies.
 * "Il mio Piano" tab content for agency owners.
 *
 * Sprint dashboard_alignment_v1 (26/05/26 22:00):
 * - Removed single-company AI Credits widget (was misleading on a
 *   portfolio view); replaced with PortfolioAiCreditsWidget (Σ across
 *   all clients).
 * - Added cross-portfolio views previously missing: TrendChart,
 *   TopPositionsTable (with company column), TimeInStateChart,
 *   Recent Applications (with company badge).
 * - useAgencyAggregatedData now sources clients from `user.agency.clients`
 *   (filter parity with BE listAgencyClients: admin + isActive) so the
 *   portfolio numbers match the `clientsUsed` counter in the header.
 * - TopPositions row click: switches the active company via AuthContext
 *   before navigating, so the URL resolves to the correct tenant.
 */
export function AgencyPlanDashboard({ period }: { period: Period }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, setSelectedCompany } = useAuth();
  const {
    positions, postings, applications,
    companyNameById, portfolioAiCredits,
    loading: dataLoading, companiesLoaded, companiesTotal,
  } = useAgencyAggregatedData();

  const m = useDashboardMetrics(applications, positions, postings, period);

  // Map positionId → companyId via the tagged positions in
  // useAgencyAggregatedData (the `_companyId` extra field on each
  // position is set by the hook when iterating clients).
  const companyIdByPositionId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of positions as Array<typeof positions[number] & { _companyId?: string }>) {
      if (p._companyId) map[p.id] = p._companyId;
    }
    return map;
  }, [positions]);

  // Enrich topPositions with companyName + companyId for cross-portfolio
  // rendering and tenant-aware navigation.
  const topPositionsCrossPortfolio: TopPositionsRow[] = useMemo(
    () => m.topPositions.map(p => {
      const companyId = companyIdByPositionId[p.positionId];
      return {
        ...p,
        ...(companyId ? { companyId, companyName: companyNameById[companyId] } : {}),
      };
    }),
    [m.topPositions, companyIdByPositionId, companyNameById],
  );

  // Recent applications cross-portfolio — top 10 by appliedAt in the
  // selected period (consistent with the SME Dashboard rule applied
  // by sprint dashboard_alignment_v1).
  const recentAppsCrossPortfolio = useMemo(() => {
    const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : null;
    let pool = applications.filter((a) => !isApplicationArchived(a));
    if (days !== null) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      cutoff.setHours(0, 0, 0, 0);
      pool = pool.filter(a => new Date(a.appliedAt) >= cutoff);
    }
    return [...pool]
      .sort((a, b) => new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime())
      .slice(0, 10);
  }, [applications, period]);

  const isLoading = dataLoading;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="relative">
          <div className="h-12 w-12 rounded-full border-4 border-muted" />
          <Loader2 className="absolute inset-0 h-12 w-12 animate-spin text-primary" />
        </div>
        <div className="text-center space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t('dashboard.loading_plan_data')}
          </p>
          {dataLoading && companiesTotal > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('dashboard.loading_companies', { loaded: companiesLoaded, total: companiesTotal })}
            </p>
          )}
        </div>
      </div>
    );
  }

  /** Switch active company in AuthContext then route — preserves tenant. */
  const handleCrossCompanyNavigate = (row: TopPositionsRow) => {
    if (!row.companyId) return;
    const membership = user?.memberships?.[row.companyId];
    if (membership?.company) {
      setSelectedCompany(membership.company);
    }
    navigate(`/job-positions/${row.positionId}`);
  };

  // ── Recent apps table columns (cross-portfolio, with client badge) ──
  const recentAppsColumns: ResponsiveTableColumn<Application>[] = [
    {
      id: 'candidate',
      label: t('dashboard.candidate'),
      priority: 'primary',
      alwaysVisible: true,
      ...TABLE_COL.title,
      render: (row) => (
        <span className="font-medium truncate block">
          {row.candidateFirstName} {row.candidateLastName}
        </span>
      ),
    },
    {
      id: 'client',
      label: t('dashboard.client'),
      priority: 'secondary',
      ...TABLE_COL.text,
      render: (row) => {
        const cid = companyIdByPositionId[row.positionId];
        return (
          <span className="text-xs text-muted-foreground truncate block">
            {cid ? (companyNameById[cid] ?? '—') : '—'}
          </span>
        );
      },
    },
    {
      id: 'position',
      label: t('dashboard.position'),
      priority: 'secondary',
      ...TABLE_COL.text,
      render: (row) => (
        <span className="text-xs text-muted-foreground truncate block" title={row.positionTitle}>
          {row.positionTitle}
        </span>
      ),
    },
    {
      id: 'appliedAt',
      label: t('dashboard.applied'),
      priority: 'tertiary',
      ...TABLE_COL.date,
      render: (row) => (
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatRelative(row.appliedAt)}
        </span>
      ),
    },
    {
      id: 'channel',
      label: t('dashboard.channel'),
      priority: 'tertiary',
      ...TABLE_COL.channel,
      render: (row) => <ChannelBadge channel={row.channel} />,
    },
    {
      id: 'score',
      label: t('dashboard.score'),
      priority: 'tertiary',
      ...TABLE_COL.score,
      render: (row) => <ScoreBadge score={row.aiScore} />,
    },
    {
      id: 'state',
      label: t('dashboard.status'),
      priority: 'primary',
      alwaysVisible: true,
      ...TABLE_COL.status,
      render: (row) => <StatusBadge state={row.state} />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Aggregated Hero Metrics */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">{t('dashboard.aggregated_metrics')}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <HeroMetricCard
            label={t('dashboard.openPositions')}
            value={m.openPositions}
            icon={Briefcase}
            color="text-primary"
            badge={m.agingPositions}
            badgeColor="bg-warning/10 text-warning"
            tooltip={t('dashboard.tooltip.openPositions', { count: m.openPositions, aging: m.agingPositions })}
          />
          <HeroMetricCard
            label={t('dashboard.activePostings')}
            value={m.activePostings}
            icon={Megaphone}
            color="text-success"
            tooltip={t('dashboard.tooltip.activePostings')}
          />
          <HeroMetricCard
            label={t('dashboard.applications')}
            value={m.periodAppCount}
            icon={Users}
            color="text-muted-foreground"
            change={m.appChange}
            tooltip={t('dashboard.tooltip.applications', { total: applications.length })}
          />
          <HeroMetricCard
            label={t('dashboard.offerAcceptance')}
            value={m.oar !== null ? `${(m.oar * 100).toFixed(0)}%` : '—'}
            icon={Percent}
            color="text-success"
            tooltip={t('dashboard.tooltip.offerAcceptance')}
          />
          <HeroMetricCard
            label={t('dashboard.medianTimeToHire')}
            value={m.medianTTH !== null ? `${m.medianTTH.toFixed(1)}d` : '—'}
            icon={Hourglass}
            color="text-primary"
            semaphore={m.tthSemaphore}
            tooltip={t('dashboard.tooltip.medianTimeToHire')}
          />
          <HeroMetricCard
            label={t('dashboard.medianTimeToFill')}
            value={m.medianTTF !== null ? `${m.medianTTF.toFixed(1)}d` : '—'}
            icon={Target}
            color="text-primary"
            semaphore={m.ttfSemaphore}
            tooltip={t('dashboard.tooltip.medianTimeToFill')}
          />
        </div>
      </section>

      <PipelineStatusGrid key={period} counts={m.pipelineStatus} />

      {/* Plan limits + portfolio AI Credits */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">{t('dashboard.plan_limits')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <AgencyClientsCapWidget />
          <AgencyJobsCapWidget />
          <AgencyTranscriptsWidget />
          <PortfolioAiCreditsWidget data={portfolioAiCredits} />
        </div>
      </section>

      {/* Aggregated Funnel + Channel */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">{t('dashboard.aggregated_funnel')}</h2>
        <div className="grid lg:grid-cols-2 gap-6 min-w-0">
          <FunnelChart data={m.funnelWithRates} outcomes={m.funnelOutcomes} className="h-full" />
          <ChannelChart data={m.channelData} className="h-full" />
        </div>
      </section>

      {/* Conversion KPIs */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">{t('dashboard.conversion_kpis')}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="border-border/50">
            <CardContent className="pt-3 pb-2.5 px-4">
              <p className="text-[11px] text-muted-foreground mb-0.5">{t('dashboard.applicantToInterview')}</p>
              <p className="text-base font-semibold">{m.applicantToInterview !== null ? `${m.applicantToInterview.toFixed(1)}%` : '—'}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-3 pb-2.5 px-4">
              <p className="text-[11px] text-muted-foreground mb-0.5">{t('dashboard.interviewToHire')}</p>
              <p className="text-base font-semibold">{m.interviewToHire !== null ? `${m.interviewToHire.toFixed(1)}%` : '—'}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-3 pb-2.5 px-4">
              <p className="text-[11px] text-muted-foreground mb-0.5">{t('dashboard.applicantToHire')}</p>
              <p className="text-base font-semibold text-success">{m.applicantToHire !== null ? `${m.applicantToHire.toFixed(1)}%` : '—'}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-3 pb-2.5 px-4">
              <p className="text-[11px] text-muted-foreground mb-0.5">{t('dashboard.applicantToReject')}</p>
              <p className="text-base font-semibold text-destructive">{m.applicantToReject !== null ? `${m.applicantToReject.toFixed(1)}%` : '—'}</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Top positions cross-portfolio + Time in state */}
      <div className="grid lg:grid-cols-2 gap-6">
        <TopPositionsTable
          data={topPositionsCrossPortfolio}
          pipelineErrors={m.pipelineErrors}
          title={t('dashboard.aggregated_topPositions')}
          onCrossCompanyNavigate={handleCrossCompanyNavigate}
        />
        <TimeInStateChart data={m.timeInStates} />
      </div>

      {/* Trend cross-portfolio */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">{t('dashboard.aggregated_trend')}</CardTitle>
        </CardHeader>
        <CardContent>
          <TrendChart data={m.trendData} />
        </CardContent>
      </Card>

      {/* Recent applications cross-portfolio */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">{t('dashboard.aggregated_recentApps')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {recentAppsCrossPortfolio.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8 px-5">{t('dashboard.noRecent')}</p>
          ) : (
            <ResponsiveTable
              data={recentAppsCrossPortfolio}
              columns={recentAppsColumns}
              getRowKey={(row) => row.id}
              onRowClick={(row) => {
                const cid = companyIdByPositionId[row.positionId];
                if (cid) {
                  const membership = user?.memberships?.[cid];
                  if (membership?.company) setSelectedCompany(membership.company);
                }
                navigate(row.positionId
                  ? `/job-positions/${row.positionId}/applications/${row.id}`
                  : `/applications/${row.id}`,
                );
              }}
              storageKey="agency-recent-apps"
              ariaLabel={t('dashboard.aggregated_recentApps')}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AgencyPlanDashboard;
